import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import {
  BadgeCheck, Briefcase, Building2, Calendar, Clock3, Eye, EyeOff, FileText, Globe, Link2, Mail, MapPin,
  Pencil, Phone, Plus, Receipt, Save, Upload, User, Users,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useProfileStore } from '@/store/profileStore'
import { getProfile, updateProfile, uploadImage, uploadResume, deleteResume, downloadResume, type IndividualProfile, type CompanyProfile } from '@/services/profileService'
import { getEnums } from '@/services/jobService'
import { useProvinces, normalizeProvince } from '@/services/addressService'
import SearchableSelect from '@/components/ui/SearchableSelect'
import { companySizeLabel } from '@/lib/companySize'
import ImageUploader from '@/components/ui/ImageUploader'
import { Field, inputClass, textareaClass, readonlyInputClass } from '@/components/ui/FormField'
import { filterPhone, filterTaxId, isValidPhone, isValidTaxId } from '@/lib/inputFilters'

// หน้าโปรไฟล์ (บุคคลทั่วไป + บริษัท) — แก้ไขในหน้านี้เลย แยกเป็นการ์ดตามหัวข้อ กด "แก้ไข" ทีละการ์ดแล้วบันทึกเฉพาะส่วนนั้น
// (เดิมมีหน้าแยก /profile/edit — ยกเลิกแล้ว route เก่า redirect มาที่นี่)

function isIndividual(p: IndividualProfile | CompanyProfile): p is IndividualProfile {
  return 'first_name' in p
}

type SectionKey = 'personal' | 'about' | 'apply' | 'company' | 'companyAbout' | 'tax'
type Draft = Record<string, string>

function formatThaiDate(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' })
}

// ── การ์ดหัวข้อ: โหมดดู (rows) ↔ โหมดแก้ไข (ฟอร์ม + ปุ่มยกเลิก/บันทึก) ──
function SectionCard({ title, editing, disabled, saving, error, onEdit, onCancel, onSave, view, form, className }: {
  title: string
  editing: boolean
  disabled?: boolean          // กำลังแก้การ์ดอื่นอยู่ — กันแก้พร้อมกันหลายการ์ด
  saving: boolean
  error: string
  onEdit: () => void
  onCancel: () => void
  onSave: () => void
  view: (onEdit: () => void) => ReactNode
  form: ReactNode
  className?: string
}) {
  return (
    <section className={`flex h-full flex-col border border-[#E4E1F0] border-t-2 border-t-accent bg-white ${className ?? ''}`}>
      <div className="flex min-h-[64px] items-center justify-between border-b border-[#EEECF6] px-6 py-4">
        <h2 className="text-[13px] font-semibold uppercase tracking-[0.08em] text-[#19172A]">{title}</h2>
        {!editing && (
          <button
            onClick={onEdit}
            disabled={disabled}
            className="flex items-center gap-1.5 border border-[#E4E1F0] px-3 py-1.5 text-xs font-medium text-[#4B4560] transition-colors hover:border-accent hover:text-accent disabled:pointer-events-none disabled:opacity-40"
          >
            <Pencil className="h-3.5 w-3.5" />
            แก้ไข
          </button>
        )}
      </div>

      {editing ? (
        <form onSubmit={(e) => { e.preventDefault(); onSave() }} className="p-6">
          <div className="space-y-4">{form}</div>
          {error && <p className="mt-4 text-sm text-rose-500">{error}</p>}
          <div className="mt-6 flex justify-end gap-3 border-t border-[#EEECF6] pt-5">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="border border-[#E4E1F0] px-5 py-2.5 text-sm font-medium text-[#69738A] transition-colors hover:bg-[#F5F3FC] hover:text-[#19172A]"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-accent px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent2 disabled:pointer-events-none disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {saving ? 'กำลังบันทึก...' : 'บันทึก'}
            </button>
          </div>
        </form>
      ) : (
        <div className="px-6 py-2">{view(onEdit)}</div>
      )}
    </section>
  )
}

// รายการข้อมูลแบบแถว: ไอคอน + ชื่อหัวข้อ (ซ้าย) / ค่า (ขวา) คั่นด้วยเส้น — ไม่ใช่หน้าตาฟอร์ม
// ช่องที่ยังว่างโชว์ "+ เพิ่มข้อมูล" กดแล้วเปิดโหมดแก้ไขของการ์ดนั้น
interface InfoItem { icon: LucideIcon; label: string; value: ReactNode; long?: boolean }

function InfoList({ items, onAdd }: { items: InfoItem[]; onAdd: () => void }) {
  return (
    <dl className="divide-y divide-[#EEECF6]">
      {items.map(({ icon: Icon, label, value, long }) => (
        // มือถือ: หัวข้ออยู่บน ค่าอยู่ล่าง (เดิมเรียงข้างกันแล้วค่าถูกบีบเป็นแคบๆ) / จอใหญ่: ซ้าย-ขวาเหมือนเดิม
        <div key={label} className={`flex flex-col gap-1 py-4 sm:flex-row sm:gap-4 ${long ? 'sm:items-start' : 'sm:items-center'}`}>
          <dt className="flex shrink-0 items-center gap-2.5 text-sm text-[#69738A] sm:w-44">
            <Icon className="h-4 w-4 text-accent/70" strokeWidth={1.75} />
            {label}
          </dt>
          <dd className="min-w-0 flex-1 whitespace-pre-line break-words pl-[26px] text-sm font-medium leading-relaxed text-[#19172A] sm:pl-0">
            {value || (
              <button onClick={onAdd} className="inline-flex items-center gap-1 font-normal text-[#69738A] hover:text-accent">
                <Plus className="h-3.5 w-3.5" /> เพิ่มข้อมูล
              </button>
            )}
          </dd>
        </div>
      ))}
    </dl>
  )
}

export default function ProfileTab() {
  const [profile, setProfile] = useState<IndividualProfile | CompanyProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [editing, setEditing] = useState<SectionKey | null>(null)
  const [draft, setDraft] = useState<Draft>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [savingVisibility, setSavingVisibility] = useState(false)
  const [sizeOptions, setSizeOptions] = useState<string[]>([])
  const [pendingImage, setPendingImage] = useState<File | null>(null)   // รูปโปรไฟล์/โลโก้ที่เลือกไว้ รออัปโหลดตอนกดบันทึกของการ์ด
  const [resumeBusy, setResumeBusy] = useState(false)
  const [resumeError, setResumeError] = useState('')
  const [pendingResume, setPendingResume] = useState<File | null>(null)   // ไฟล์ใหม่ที่เลือกไว้ รอกดบันทึก
  const [removeResume, setRemoveResume] = useState(false)                  // ทำเครื่องหมายลบเรซูเม่เดิม รอกดบันทึก
  const resumeInputRef = useRef<HTMLInputElement>(null)
  const { data: provinces } = useProvinces()

  useEffect(() => {
    getEnums().then((d) => setSizeOptions(d.CompanySize || [])).catch(() => setSizeOptions([]))
    getProfile()
      .then(setProfile)
      .catch((err) => setLoadError(err instanceof Error ? err.message : 'โหลดโปรไฟล์ไม่สำเร็จ'))
      .finally(() => setLoading(false))
  }, [])

  // บันทึก/เปลี่ยนรูป/เปลี่ยนชื่อสำเร็จเมื่อไหร่ ให้ Navbar (ชื่อ รูป อีเมลมุมขวาบน) อัปเดตตามทันที
  useEffect(() => {
    if (profile) useProfileStore.getState().setFromProfile(profile)
  }, [profile])

  if (loading) return <p className="text-sm text-[#69738A]">กำลังโหลดโปรไฟล์...</p>
  if (loadError || !profile) return <p className="text-sm text-rose-500">{loadError || 'ไม่พบข้อมูลโปรไฟล์'}</p>

  const set = (key: string, value: string) => setDraft((d) => ({ ...d, [key]: value }))
  const v = (key: string) => draft[key] ?? ''

  const startEdit = (key: SectionKey, initial: Draft) => {
    setPendingImage(null)
    setDraft(initial)
    setError('')
    setEditing(key)
  }
  const cancelEdit = () => { setEditing(null); setPendingImage(null); setError('') }

  const save = async (payload: Partial<IndividualProfile & CompanyProfile>) => {
    setSaving(true)
    setError('')
    try {
      // รูปที่เลือกไว้ (ยังไม่ได้อัปโหลด) — อัปโหลดตอนกดบันทึก ล้มเหลว = ไม่บันทึกอะไรเลย
      let body = payload
      if (pendingImage) {
        const key = 'avatar_url' in payload ? 'avatar_url' : 'logo_url'
        body = { ...payload, [key]: await uploadImage(pendingImage) }
      }
      setProfile(await updateProfile(body))
      setPendingImage(null)
      setEditing(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'บันทึกโปรไฟล์ไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  const runResume = async (action: () => Promise<IndividualProfile | CompanyProfile | void>) => {
    setResumeBusy(true)
    setResumeError('')
    try {
      const next = await action()
      if (next) setProfile(next)
    } catch (err) {
      setResumeError(err instanceof Error ? err.message : 'ดำเนินการไม่สำเร็จ')
    } finally {
      setResumeBusy(false)
      if (resumeInputRef.current) resumeInputRef.current.value = ''
    }
  }

  // เรซูเม่: เลือกไฟล์/กดลบ = รอบันทึก (ยังไม่มีผลกับเซิร์ฟเวอร์) จนกว่าจะกด "บันทึก"
  const pickResume = (f: File | undefined) => {
    if (!f) return
    setResumeError('')
    const ext = f.name.split('.').pop()?.toLowerCase() ?? ''
    if (!['pdf', 'doc', 'docx'].includes(ext)) { setResumeError('รองรับเฉพาะไฟล์ PDF, DOC หรือ DOCX'); return }
    if (f.size > 10 * 1024 * 1024) { setResumeError('ไฟล์ต้องมีขนาดไม่เกิน 10 MB'); return }
    setPendingResume(f)
    setRemoveResume(false)
  }
  const cancelResume = () => { setPendingResume(null); setRemoveResume(false); setResumeError('') }
  const saveResume = () => runResume(async () => {
    let next: IndividualProfile | CompanyProfile | undefined
    if (removeResume && !pendingResume) next = await deleteResume()
    if (pendingResume) next = await uploadResume(pendingResume)   // อัปโหลดไฟล์ใหม่ = แทนที่ไฟล์/ลิงก์เดิม
    setPendingResume(null)
    setRemoveResume(false)
    return next
  })

  const need = (...pairs: [string, string][]): boolean => {
    const missing = pairs.find(([key]) => !v(key).trim())
    if (missing) { setError(`กรุณากรอก${missing[1]}`); return false }
    return true
  }

  const cardProps = (key: SectionKey) => ({
    editing: editing === key,
    disabled: editing !== null && editing !== key,
    saving,
    error,
    onCancel: cancelEdit,
  })

  // ═══════════════ บุคคลทั่วไป ═══════════════
  if (isIndividual(profile)) {
    const p = profile
    const name = `${p.first_name} ${p.last_name}`

    const toggleVisibility = async () => {
      setSavingVisibility(true)
      try { setProfile(await updateProfile({ is_open_to_work: !p.is_open_to_work })) }
      catch { /* เงียบไว้ — ค่าเดิมยังอยู่ */ }
      finally { setSavingVisibility(false) }
    }

    return (
      <div className="space-y-5">
        <ProfileHeader name={name} email={p.email} imageUrl={p.avatar_url} badges={
          p.is_open_to_work ? <Badge tone="green" icon={<Eye className="h-3.5 w-3.5" />}>เปิดให้ผู้ว่าจ้างมองเห็น</Badge> : null
        } />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <SectionCard
            title="ข้อมูลส่วนตัว"
            {...cardProps('personal')}
            onEdit={() => startEdit('personal', {
              avatar_url: p.avatar_url ?? '', first_name: p.first_name, last_name: p.last_name, phone: p.phone ?? '',
              province: normalizeProvince(p.province), date_of_birth: p.date_of_birth ?? '',
            })}
            onSave={() => {
              if (!need(['first_name', 'ชื่อ'], ['last_name', 'นามสกุล'])) return
              if (v('phone').trim() && !isValidPhone(v('phone').trim())) { setError('เบอร์โทรศัพท์ต้องเป็นตัวเลข 9–10 หลัก ขึ้นต้นด้วย 0'); return }
              save({
                avatar_url: v('avatar_url') || null, first_name: v('first_name').trim(), last_name: v('last_name').trim(),
                phone: v('phone').trim() || null, province: v('province') || null, date_of_birth: v('date_of_birth') || null,
              })
            }}
            view={(add) => <InfoList onAdd={add} items={[
              { icon: User, label: 'ชื่อ-นามสกุล', value: name },
              { icon: Mail, label: 'อีเมล', value: p.email },
              { icon: Phone, label: 'เบอร์โทรศัพท์', value: p.phone },
              { icon: MapPin, label: 'จังหวัดที่อยู่ปัจจุบัน', value: normalizeProvince(p.province) },
              { icon: Calendar, label: 'วันเกิด', value: formatThaiDate(p.date_of_birth) },
            ]} />}
            form={<>
              <ImageUploader label="รูปโปรไฟล์" value={pendingImage ?? (v('avatar_url') || null)} fallback={p.first_name[0]} round
                onChange={(x) => { if (x instanceof File) setPendingImage(x); else { setPendingImage(null); set('avatar_url', x) } }} />
              <Field label="อีเมล" htmlFor="email"><input id="email" value={p.email} disabled className={readonlyInputClass} /></Field>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="ชื่อ" htmlFor="first_name" required>
                  <input id="first_name" value={v('first_name')} onChange={(e) => set('first_name', e.target.value)} className={inputClass} />
                </Field>
                <Field label="นามสกุล" htmlFor="last_name" required>
                  <input id="last_name" value={v('last_name')} onChange={(e) => set('last_name', e.target.value)} className={inputClass} />
                </Field>
                <Field label="เบอร์โทรศัพท์" htmlFor="phone">
                  <input id="phone" value={v('phone')} onChange={(e) => set('phone', filterPhone(e.target.value))} inputMode="tel" maxLength={10} className={inputClass} />
                </Field>
                <Field label="จังหวัดที่อยู่ปัจจุบัน" htmlFor="province">
                  <SearchableSelect
                    id="province" value={v('province')} options={provinces} onChange={(val) => set('province', val)}
                    className={inputClass} placeholder="เลือกจังหวัด..." searchPlaceholder="ค้นหาจังหวัด..."
                  />
                </Field>
                <Field label="วันเกิด" htmlFor="dob">
                  <input id="dob" type="date" value={v('date_of_birth')} onChange={(e) => set('date_of_birth', e.target.value)} className={inputClass} />
                </Field>
              </div>
            </>}
          />

          <SectionCard
            title="เกี่ยวกับฉัน"
            {...cardProps('about')}
            onEdit={() => startEdit('about', {
              bio: p.bio ?? '', current_job_title: p.current_job_title ?? '', years_experience: String(p.years_experience ?? 0),
            })}
            onSave={() => save({
              bio: v('bio').trim() || null, current_job_title: v('current_job_title').trim() || null,
              years_experience: Math.max(0, Number(v('years_experience')) || 0),
            })}
            view={(add) => <InfoList onAdd={add} items={[
              { icon: FileText, label: 'แนะนำตัวสั้นๆ', value: p.bio, long: true },
              { icon: Briefcase, label: 'ตำแหน่งงานปัจจุบัน', value: p.current_job_title },
              { icon: Briefcase, label: 'ประสบการณ์', value: `${p.years_experience ?? 0} ปี` },
            ]} />}
            form={<>
              <Field label="แนะนำตัวสั้นๆ" htmlFor="bio">
                <textarea id="bio" rows={4} value={v('bio')} onChange={(e) => set('bio', e.target.value)} placeholder="เล่าเกี่ยวกับตัวคุณสั้นๆ..." className={textareaClass} />
              </Field>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="ตำแหน่งงานปัจจุบัน" htmlFor="job_title">
                  <input id="job_title" value={v('current_job_title')} onChange={(e) => set('current_job_title', e.target.value)} className={inputClass} />
                </Field>
                <Field label="ประสบการณ์ (ปี)" htmlFor="years">
                  <input id="years" type="number" min={0} value={v('years_experience')} onChange={(e) => set('years_experience', e.target.value)} className={inputClass} />
                </Field>
              </div>
            </>}
          />

          <SectionCard
            title="โปรไฟล์สำหรับสมัครงาน"
            {...cardProps('apply')}
            onEdit={() => startEdit('apply', { linkedin_url: p.linkedin_url ?? '' })}
            onSave={() => save({ linkedin_url: v('linkedin_url').trim() || null })}
            view={(add) => <InfoList onAdd={add} items={[
              { icon: Link2, label: 'LinkedIn', value: p.linkedin_url ? <a href={p.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">{p.linkedin_url}</a> : null },
            ]} />}
            form={<>
              <Field label="LinkedIn URL" htmlFor="linkedin">
                <input id="linkedin" value={v('linkedin_url')} onChange={(e) => set('linkedin_url', e.target.value)} placeholder="https://linkedin.com/in/..." className={inputClass} />
              </Field>
            </>}
          />

          {/* การมองเห็น + เรซูเม่ */}
          <section className="flex h-full flex-col border border-[#E4E1F0] border-t-2 border-t-accent bg-white">
            <h2 className="flex min-h-[64px] items-center border-b border-[#EEECF6] px-6 py-4 text-[13px] font-semibold uppercase tracking-[0.08em] text-[#19172A]">การมองเห็นและไฟล์แนบ</h2>
            <div className="px-6 py-2">

            <div className="flex items-center justify-between gap-4 border-b border-[#EEECF6] py-4">
              <div className="flex items-center gap-2 text-sm text-[#19172A]">
                {p.is_open_to_work ? <Eye className="h-4 w-4 text-emerald-600" /> : <EyeOff className="h-4 w-4 text-[#69738A]" />}
                เปิดให้ผู้ว่าจ้างมองเห็นโปรไฟล์
              </div>
              <button
                onClick={toggleVisibility}
                disabled={savingVisibility}
                aria-label="สลับการมองเห็นโปรไฟล์"
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${p.is_open_to_work ? 'bg-accent' : 'bg-[#E9E7F2]'}`}
              >
                <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${p.is_open_to_work ? 'translate-x-[18px]' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* เรซูเม่ — อัปโหลดเป็นไฟล์ (PDF/DOC/DOCX ≤ 10 MB) เก็บส่วนตัว ดาวน์โหลดได้เฉพาะเจ้าของ/แอดมิน — เลือก/ลบแล้วต้องกด "บันทึก" */}
            <div className="py-4">
              <p className="mb-2.5 text-sm font-medium text-[#19172A]">เรซูเม่</p>
              <input ref={resumeInputRef} type="file" accept=".pdf,.doc,.docx" className="hidden"
                onChange={(e) => { pickResume(e.target.files?.[0]); e.target.value = '' }} />
              {(p.has_resume_file || p.resume_url) && (
                <div className={`mb-2 flex flex-wrap items-center gap-3 border border-[#E7E5F0] bg-[#FAFAFF] px-4 py-3 ${removeResume ? 'opacity-60' : ''}`}>
                  <FileText className="h-4 w-4 text-accent" />
                  {p.has_resume_file ? (
                    <button type="button" disabled={resumeBusy} onClick={() => void runResume(() => downloadResume())}
                      className={`text-sm font-medium text-accent hover:underline disabled:opacity-50 ${removeResume ? 'line-through' : ''}`}>ดาวน์โหลดเรซูเม่</button>
                  ) : (
                    <a href={p.resume_url!} target="_blank" rel="noopener noreferrer" className={`max-w-[260px] truncate text-sm text-accent hover:underline ${removeResume ? 'line-through' : ''}`}>{p.resume_url}</a>
                  )}
                  {removeResume && <span className="text-xs text-rose-500">จะถูกลบเมื่อกดบันทึก</span>}
                  <button type="button" disabled={resumeBusy} onClick={() => { setRemoveResume((v) => !v); setPendingResume(null) }}
                    className={`ml-auto text-xs hover:underline disabled:opacity-50 ${removeResume ? 'text-[#69738A]' : 'text-rose-500'}`}>
                    {removeResume ? 'ยกเลิกการลบ' : 'ลบ'}
                  </button>
                </div>
              )}
              {pendingResume && (
                <div className="mb-2 flex flex-wrap items-center gap-3 border border-accent/30 bg-accent/5 px-4 py-3">
                  <Upload className="h-4 w-4 text-accent" />
                  <span className="min-w-0 truncate text-sm text-[#19172A]">{pendingResume.name}</span>
                  <span className="text-xs text-accent">ไฟล์ใหม่ — รอกดบันทึก</span>
                  <button type="button" disabled={resumeBusy} onClick={() => setPendingResume(null)} className="ml-auto text-xs text-[#69738A] hover:underline">เอาออก</button>
                </div>
              )}
              {!pendingResume && !removeResume && (
                <button type="button" disabled={resumeBusy} onClick={() => resumeInputRef.current?.click()}
                  className="flex min-h-[72px] w-full cursor-pointer flex-col items-center justify-center border border-dashed border-accent/30 bg-white px-6 py-3 text-center transition-colors hover:border-accent/60 hover:bg-accent/5 disabled:opacity-50">
                  <Upload className="mb-1 h-5 w-5 text-[#69738A]" strokeWidth={1.5} />
                  <p className="text-sm text-[#19172A]">{(p.has_resume_file || p.resume_url) ? 'เลือกไฟล์ใหม่เพื่อแทนที่' : 'คลิกเพื่อเลือกเรซูเม่'}</p>
                  <p className="mt-0.5 text-xs text-[#69738A]">PDF, DOC, DOCX · ไม่เกิน 10 MB</p>
                </button>
              )}
              {(pendingResume || removeResume) && (
                <div className="mt-3 flex items-center gap-3">
                  <button type="button" disabled={resumeBusy} onClick={() => void saveResume()}
                    className="rounded-full bg-accent px-6 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
                    {resumeBusy ? 'กำลังบันทึก...' : 'บันทึก'}
                  </button>
                  <button type="button" disabled={resumeBusy} onClick={cancelResume} className="text-sm text-[#69738A] hover:underline">ยกเลิก</button>
                </div>
              )}
              {resumeError && <p className="mt-2 text-xs text-rose-500">{resumeError}</p>}
            </div>
            </div>
          </section>
        </div>
      </div>
    )
  }

  // ═══════════════ บริษัท ═══════════════
  const c = profile

  return (
    <div className="space-y-5">
      <ProfileHeader
        name={c.company_name}
        email={c.email}
        imageUrl={c.logo_url}
        badges={c.is_verified
          ? <Badge tone="green" icon={<BadgeCheck className="h-3.5 w-3.5" />}>ยืนยันตัวตนแล้ว</Badge>
          : <Badge tone="amber" icon={<Clock3 className="h-3.5 w-3.5" />}>รอการยืนยันตัวตน</Badge>}
      />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <SectionCard
          title="ข้อมูลบริษัท"
          {...cardProps('company')}
          onEdit={() => startEdit('company', {
            logo_url: c.logo_url ?? '', company_name: c.company_name, industry: c.industry ?? '',
            company_size: c.company_size ?? '', location: c.location ?? '',
          })}
          onSave={() => {
            if (!need(['company_name', 'ชื่อบริษัท'], ['industry', 'อุตสาหกรรม'], ['company_size', 'ขนาดบริษัท'], ['location', 'ที่ตั้ง'])) return
            save({
              logo_url: v('logo_url') || null, company_name: v('company_name').trim(), industry: v('industry').trim(),
              company_size: v('company_size'), location: v('location').trim(),
            })
          }}
          view={(add) => <InfoList onAdd={add} items={[
            { icon: Building2, label: 'ชื่อบริษัท', value: c.company_name },
            { icon: Mail, label: 'อีเมล', value: c.email },
            { icon: Briefcase, label: 'อุตสาหกรรม', value: c.industry },
            { icon: Users, label: 'ขนาดบริษัท', value: companySizeLabel(c.company_size) },
            { icon: MapPin, label: 'ที่ตั้ง', value: c.location },
          ]} />}
          form={<>
            <ImageUploader label="โลโก้บริษัท" value={pendingImage ?? (v('logo_url') || null)} fallback={c.company_name[0]}
              onChange={(x) => { if (x instanceof File) setPendingImage(x); else { setPendingImage(null); set('logo_url', x) } }} />
            <Field label="อีเมล" htmlFor="email"><input id="email" value={c.email} disabled className={readonlyInputClass} /></Field>
            <Field label="ชื่อบริษัท" htmlFor="company_name" required>
              <input id="company_name" value={v('company_name')} onChange={(e) => set('company_name', e.target.value)} className={inputClass} />
            </Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="อุตสาหกรรม" htmlFor="industry" required>
                <input id="industry" value={v('industry')} onChange={(e) => set('industry', e.target.value)} className={inputClass} />
              </Field>
              <Field label="ขนาดบริษัท" htmlFor="company_size" required>
                <select id="company_size" value={v('company_size')} onChange={(e) => set('company_size', e.target.value)} className={inputClass}>
                  <option value="">เลือกขนาดบริษัท...</option>
                  {sizeOptions.map((x) => <option key={x} value={x}>{companySizeLabel(x)}</option>)}
                </select>
              </Field>
            </div>
            <Field label="ที่ตั้ง" htmlFor="location" required>
              <input id="location" value={v('location')} onChange={(e) => set('location', e.target.value)} className={inputClass} />
            </Field>
          </>}
        />

        <SectionCard
          title="เกี่ยวกับบริษัท"
          {...cardProps('companyAbout')}
          onEdit={() => startEdit('companyAbout', { description: c.description ?? '', website: c.website ?? '' })}
          onSave={() => save({ description: v('description').trim() || null, website: v('website').trim() || null })}
          view={(add) => <InfoList onAdd={add} items={[
            { icon: FileText, label: 'แนะนำบริษัท', value: c.description, long: true },
            { icon: Globe, label: 'เว็บไซต์', value: c.website ? <a href={c.website} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">{c.website}</a> : null },
          ]} />}
          form={<>
            <Field label="แนะนำบริษัท" htmlFor="description">
              <textarea id="description" rows={6} value={v('description')} onChange={(e) => set('description', e.target.value)} placeholder="เล่าเกี่ยวกับบริษัท วัฒนธรรมองค์กร และสิ่งที่ทำ..." className={textareaClass} />
            </Field>
            <Field label="เว็บไซต์" htmlFor="website">
              <input id="website" value={v('website')} onChange={(e) => set('website', e.target.value)} placeholder="https://..." className={inputClass} />
            </Field>
          </>}
        />

        <SectionCard
          title="ข้อมูลภาษี"
          {...cardProps('tax')}
          onEdit={() => startEdit('tax', { tax_id: c.tax_id ?? '' })}
          onSave={() => { if (need(['tax_id', 'เลขประจำตัวผู้เสียภาษี'])) { if (!isValidTaxId(v('tax_id').trim())) { setError('เลขประจำตัวผู้เสียภาษีต้องเป็นตัวเลข 13 หลัก'); return } save({ tax_id: v('tax_id').trim() }) } }}
          view={(add) => <InfoList onAdd={add} items={[{ icon: Receipt, label: 'เลขประจำตัวผู้เสียภาษี', value: c.tax_id }]} />}
          form={
            <Field label="เลขประจำตัวผู้เสียภาษี" htmlFor="tax_id" required>
              <input id="tax_id" value={v('tax_id')} onChange={(e) => set('tax_id', filterTaxId(e.target.value))} inputMode="numeric" maxLength={13} className={inputClass} />
            </Field>
          }
        />
      </div>
    </div>
  )
}

// ── ส่วนหัวโปรไฟล์: แถบสีไล่ + รูป/โลโก้สี่เหลี่ยมคาบเส้นขอบแถบ + ชื่อ + อีเมล + ป้ายสถานะ ──
function ProfileHeader({ name, email, imageUrl, badges }: {
  name: string
  email: string
  imageUrl: string | null
  badges?: ReactNode
}) {
  return (
    <div className="overflow-hidden border border-[#E4E1F0] bg-white">
      <div className="h-24 bg-gradient-to-r from-[#4338CA] via-[#6366F1] to-[#8B5CF6]" />
      <div className="flex flex-wrap items-end gap-5 px-6 pb-6">
        <div className="-mt-12 flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden border-4 border-white bg-[#EEEBFF] text-4xl font-semibold text-accent shadow-sm">
          {imageUrl ? <img src={imageUrl} alt={name} className="h-full w-full object-cover" /> : name[0]}
        </div>
        <div className="min-w-0 flex-1 pt-3">
          <h2 className="truncate text-2xl font-bold text-[#19172A]">{name}</h2>
          <p className="mt-0.5 flex items-center gap-1.5 truncate text-sm text-[#69738A]">
            <Mail className="h-3.5 w-3.5" /> {email}
          </p>
        </div>
        {badges && <div className="flex flex-wrap gap-2 pb-1">{badges}</div>}
      </div>
    </div>
  )
}

function Badge({ tone, icon, children }: { tone: 'green' | 'amber'; icon: ReactNode; children: ReactNode }) {
  const cls = tone === 'green'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
    : 'border-amber-200 bg-amber-50 text-amber-700'
  return (
    <span className={`inline-flex items-center gap-1.5 border px-3 py-1.5 text-xs font-medium ${cls}`}>
      {icon}{children}
    </span>
  )
}
